import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, interval } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { DocumentService } from '@app/shared/services';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

interface SyncEntityDialogData {
  userId: string;
  vaultId?: string;
  placeholderName?: string;
  title?: string;
  placeholderText?: string;
}

@AutoUnsubscribe()
@Component({
  selector: 'sync-entity-dialog',
  templateUrl: './sync-entity-dialog.component.html',
  styleUrls: ['./sync-entity-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncEntityDialogComponent implements OnInit, OnDestroy {
  entities: any[] = [];
  allEntities: any[] = []; // Store all loaded entities
  isLoading = false;
  selectedEntity: any = null;
  subscription = new Subscription();
  searchInput = new UntypedFormControl('');
  searchValue = '';
  hasLoadedEntities = false; // Track if entities have been loaded

  constructor(
    public dialogRef: MatDialogRef<SyncEntityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SyncEntityDialogData,
    private documentService: DocumentService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Filter entities from allEntities when user types
    this.subscription.add(
      this.searchInput.valueChanges.pipe(debounce(() => interval(300))).subscribe((value: string) => {
        if (this.hasLoadedEntities) {
          // Already loaded, just filter them
          this.filterEntities(value || '');
        } else {
          // Not loaded yet, load first
          this.loadAllEntities();
        }
      })
    );
  }

  loadAllEntities() {
    if (this.hasLoadedEntities) {
      // Already loaded, just show them
      this.entities = this.filterOutSelectedEntity([...this.allEntities]);
      this.cd.detectChanges();
      return;
    }

    this.isLoading = true;
    this.cd.detectChanges();
    this.subscription.add(
      this.documentService.getEntitiesForSync(this.data?.userId, '', this.data?.vaultId).subscribe(
        (res) => {
          const list = Array.isArray(res) ? res : [];
          // Filter out placeholder entities and store in allEntities
          this.allEntities = list.filter((item) => {
            const entityType = (item?.type || '').toLowerCase();
            return entityType !== 'placeholder';
          });
          // Show all entities initially
          this.entities = this.filterOutSelectedEntity([...this.allEntities]);
          this.hasLoadedEntities = true;
          this.isLoading = false;
          this.cd.detectChanges();
        },
        () => {
          this.allEntities = [];
          this.entities = [];
          this.isLoading = false;
          this.cd.detectChanges();
        }
      )
    );
  }

  filterEntities(searchText: string) {
    this.searchValue = searchText;
    if (!searchText || searchText.trim() === '') {
      // Show all entities if search is empty
      this.entities = this.filterOutSelectedEntity([...this.allEntities]);
    } else {
      // Filter entities based on search text
      const searchLower = searchText.toLowerCase().trim();
      this.entities = this.filterOutSelectedEntity(
        this.allEntities.filter((item) => {
          const title = this.getEntityTitle(item).toLowerCase();
          const subtitle = this.getEntitySubtitle(item).toLowerCase();
          return title.includes(searchLower) || subtitle.includes(searchLower);
        })
      );
    }
    this.cd.detectChanges();
  }

  handleEntitySearch(value: string) {
    this.searchInput.setValue(value);
    this.filterEntities(value);
  }
  selectEntity(entity: any) {
    if (this.selectedEntity) {
      // Clear search input and hide entities list before showing confirmation dialog
      this.entities = [];
      this.searchValue = '';
      this.searchInput.setValue('');
      this.cd.detectChanges();

      // Show confirmation dialog before replacing
      const confirmationDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Change Entity?',
          materialIcon: 'warning',
          text: 'An entity has already been selected, do you want to replace the selected entity?',
          proceedTitle: 'Change',
          showCancelBtn: true,
        },
      });

      this.subscription.add(
        confirmationDialogRef.afterClosed().subscribe((confirmation) => {
          if (confirmation?.confirmation) {
            // User confirmed, replace with new entity
            this.selectedEntity = entity;
            this.cd.detectChanges();
          }
        })
      );
    } else {
      // No entity selected, just select the new one
      this.selectedEntity = entity;
      this.entities = [];
      this.searchValue = '';
      this.searchInput.setValue('');
      this.cd.detectChanges();
    }
  }

  onSearchFocus() {
    // Load entities when user clicks on search bar
    if (!this.hasLoadedEntities) {
      this.loadAllEntities();
    } else {
      // If already loaded, show all entities or filter based on search value
      if (this.searchInput.value) {
        this.filterEntities(this.searchInput.value);
      } else {
        this.entities = this.filterOutSelectedEntity([...this.allEntities]);
      }
      this.cd.detectChanges();
    }
  }

  clearEntity() {
    this.selectedEntity = null;
    this.cd.detectChanges();
  }

  confirmSelection() {
    this.dialogRef.close({ confirmation: true, selectedEntity: this.selectedEntity });
  }

  closeDialog() {
    this.dialogRef.close({ confirmation: false });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  getEntityTitle(entity: any): string {
    return entity?.form_name || entity?.name || this.data?.placeholderName || 'Entity';
  }

  getEntitySubtitle(entity: any): string {
    return entity?.type_template || entity?.type || '';
  }

  trackByEntity(index: number, entity: any) {
    return entity?.vault_user_form_id || entity?.id || index;
  }

  private filterOutSelectedEntity(list: any[]): any[] {
    if (!this.selectedEntity) {
      return list;
    }
    const selectedKey = this.getEntityTrackKey(this.selectedEntity);
    return list.filter((entity) => this.getEntityTrackKey(entity) !== selectedKey);
  }

  private getEntityTrackKey(entity: any) {
    return entity?.vault_user_form_id ?? entity?.id ?? entity;
  }
}
